import { convertKeyToLabel } from 'features/dynamicOntology/ontologyUtils'
import { formatRaw } from 'features/libra/llmDiff/formatFieldValue'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { FormLayoutItemSchema } from 'features/libra/sections/formLayout'
import { getVisibleSectionIds } from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/conditionalFormVisibility'
import { GeckoMeasurementUnit } from 'types'
import { z } from 'zod'

export const sourceContextStepSchema = z.object({
  id: z.string(),
  name: z.string(),
  config: z
    .object({
      sections: z
        .array(
          z.object({
            id: z.string(),
            title: z.string().optional(),
            name: z.string().optional(),
            fields: z.array(z.string()).default([]),
            field: z.string().optional(),
            zoneFieldName: z.string().optional(),
          })
        )
        .default([]),
      form: z.array(FormLayoutItemSchema).default([]),
      zoneFieldName: z.string().optional(),
    })
    .default({ sections: [], form: [] }),
})

const sourceContextTableSchema = z.object({
  title: z.string(),
  columns: z.array(z.object({ key: z.string(), title: z.string() })),
  records: z.array(z.record(z.string(), z.string())),
})

export const sourceContextSchema = z.object({
  sections: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      rows: z.array(z.object({ label: z.string(), value: z.string() })),
      tables: z.array(sourceContextTableSchema),
    })
  ),
  // Raw values retain source references; both views render the frozen sections.
  fieldValues: z.record(
    z.string(),
    z.object({ value: z.unknown().optional() }).passthrough()
  ),
})

export type SourceContext = z.infer<typeof sourceContextSchema>
export type SourceContextSection = SourceContext['sections'][number]
export type SourceContextTable = z.infer<typeof sourceContextTableSchema>

export const sourceZoneFieldSchema = z.object({
  field: z.string(),
  label: z.string(),
})

type SourceZoneField = z.infer<typeof sourceZoneFieldSchema>

const recordSchema = z.record(z.string(), z.unknown())
const measurementSchema = z
  .object({ num: z.number(), unit: z.string() })
  .strict()

function sourceRows(
  label: string,
  value: unknown,
  definition: Record<string, unknown> = {}
): SourceContextSection['rows'] {
  const measurement = measurementSchema.safeParse(value)
  if (measurement.success) {
    const { num, unit } = measurement.data
    return [
      {
        label,
        value:
          `${num} ${GeckoMeasurementUnit.GetByID(unit)?.name ?? unit}`.trim(),
      },
    ]
  }
  if (Array.isArray(value)) {
    return value.every((item) => typeof item !== 'object')
      ? [{ label, value: value.length ? value.map(formatRaw).join(', ') : '—' }]
      : []
  }
  const record = recordSchema.safeParse(value)
  if (record.success) {
    return configuredProperties(definition).flatMap(([key, child]) =>
      sourceRows(fieldLabel(key, child), record.data[key], child)
    )
  }
  return [{ label, value: formatRaw(value) }]
}

function configuredProperties(definition: Record<string, unknown>) {
  const properties = z
    .record(z.string(), recordSchema)
    .safeParse(definition.properties)
  return properties.success
    ? Object.entries(properties.data).sort(
        ([, a], [, b]) =>
          (typeof a.sortOrder === 'number' ? a.sortOrder : 0) -
          (typeof b.sortOrder === 'number' ? b.sortOrder : 0)
      )
    : []
}

function fieldLabel(key: string, definition: Record<string, unknown>) {
  return typeof definition.name === 'string'
    ? definition.name
    : convertKeyToLabel(key)
}

function sourceTable(
  title: string,
  value: unknown,
  definition: Record<string, unknown>
): SourceContextTable | undefined {
  const items = recordSchema.safeParse(definition.items)
  if (!items.success) return undefined
  const properties = configuredProperties(items.data)
  if (!properties.length) return undefined
  const records = z.array(recordSchema).safeParse(value)
  return {
    title,
    columns: properties.map(([key, child]) => ({
      key,
      title: fieldLabel(key, child),
    })),
    records: records.success
      ? records.data.map((record) =>
          Object.fromEntries(
            properties.map(([key, child]) => [
              key,
              sourceRows('', record[key], child)
                .map((row) => row.value)
                .join(', ') || '—',
            ])
          )
        )
      : [],
  }
}

export function buildSourceContext(
  steps: z.infer<typeof sourceContextStepSchema>[],
  definitions: Record<string, Record<string, unknown>>,
  fieldValues: FieldValues,
  zoneFieldName: string
): SourceContext {
  const capturedValues: FieldValues = {}
  const sections = steps.flatMap((step) =>
    getVisibleSectionIds(step.config.form, fieldValues).flatMap((id) => {
      const section = step.config.sections.find((item) => item.id === id)
      if (!section) return []
      const fields = new Set([
        ...section.fields,
        section.field,
        section.zoneFieldName,
      ])
      const rows: SourceContextSection['rows'] = []
      const tables: SourceContextTable[] = []
      for (const field of fields) {
        if (!field || field === zoneFieldName) continue
        const definition = definitions[field]
        if (!definition) continue
        const label = fieldLabel(field, definition)
        const captured = fieldValues[field]
        if (captured) capturedValues[field] = captured
        const table = sourceTable(label, captured?.value, definition)
        if (table) tables.push(table)
        else rows.push(...sourceRows(label, captured?.value, definition))
      }
      return rows.length || tables.length
        ? [
            {
              id: `${step.id}/${id}`,
              title: section.title ?? section.name ?? step.name,
              rows,
              tables,
            },
          ]
        : []
    })
  )
  return { sections, fieldValues: capturedValues }
}

export function buildSourceZoneSections(
  capturedZones: unknown,
  zoneFields: SourceZoneField[] = []
): SourceContextSection[] {
  const zones = z.array(recordSchema).parse(capturedZones)
  return zones
    .filter((zone) => zone.ai_suggested !== true)
    .map((zone, index) => ({
      id: `zone/${zone.id}`,
      title: typeof zone.title === 'string' ? zone.title : `Zone ${index + 1}`,
      rows: zoneFields.flatMap(({ field, label }) => {
        const value = zone[field]
        return value === undefined || value === null || value === ''
          ? []
          : sourceRows(label, value)
      }),
      tables: [],
    }))
    .filter((section) => section.rows.length)
}
